import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import type { Contact, ContactListFull, CreateContactListPayload, UpdateContactListPayload } from "@/types/contacts";
import { contactListService } from "@/lib/api-services/contact-list-service";

interface ContactListState {
  lists: ContactListFull[];
  currentList: ContactListFull | null;
  listContacts: Contact[];
  listContactsTotalCount: number;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;

  fetchLists: (accountId: string, params?: { page?: number; pageSize?: number; isActive?: boolean }) => Promise<void>;
  fetchList: (accountId: string, listId: string) => Promise<void>;
  createList: (accountId: string, data: CreateContactListPayload) => Promise<ContactListFull>;
  updateList: (accountId: string, listId: string, data: UpdateContactListPayload) => Promise<ContactListFull>;
  deleteList: (accountId: string, listId: string) => Promise<void>;
  fetchListContacts: (accountId: string, listId: string, params?: { page?: number; pageSize?: number }) => Promise<void>;
  addContacts: (accountId: string, listId: string, contactIds: string[]) => Promise<void>;
  removeContacts: (accountId: string, listId: string, contactIds: string[]) => Promise<void>;
  reset: () => void;
}

export const useContactListStore = create<ContactListState>()(
  immer((set) => ({
    lists: [],
    currentList: null,
    listContacts: [],
    listContactsTotalCount: 0,
    isLoading: false,
    isSaving: false,
    error: null,

    fetchLists: async (accountId, params) => {
      set((state) => { state.isLoading = true; state.error = null; });
      try {
        const result = await contactListService.getContactLists(accountId, { pageSize: 100, ...params });
        set((state) => {
          state.lists = result.items as unknown as ContactListFull[];
          state.isLoading = false;
        });
      } catch (err) {
        set((state) => {
          state.isLoading = false;
          state.error = err instanceof Error ? err.message : "Failed to load lists";
        });
      }
    },

    fetchList: async (accountId, listId) => {
      set((state) => { state.isLoading = true; state.error = null; });
      try {
        const list = await contactListService.getList(accountId, listId);
        set((state) => { state.currentList = list; state.isLoading = false; });
      } catch (err) {
        set((state) => {
          state.isLoading = false;
          state.error = err instanceof Error ? err.message : "Failed to load list";
        });
      }
    },

    createList: async (accountId, data) => {
      set((state) => { state.isSaving = true; state.error = null; });
      try {
        const list = await contactListService.createList(accountId, data);
        set((state) => { state.lists.unshift(list); state.isSaving = false; });
        return list;
      } catch (err) {
        set((state) => {
          state.isSaving = false;
          state.error = err instanceof Error ? err.message : "Failed to create list";
        });
        throw err;
      }
    },

    updateList: async (accountId, listId, data) => {
      set((state) => { state.isSaving = true; state.error = null; });
      try {
        const updated = await contactListService.updateList(accountId, listId, data);
        set((state) => {
          const idx = state.lists.findIndex((l) => l.id === listId);
          if (idx !== -1) state.lists[idx] = updated;
          if (state.currentList?.id === listId) state.currentList = updated;
          state.isSaving = false;
        });
        return updated;
      } catch (err) {
        set((state) => {
          state.isSaving = false;
          state.error = err instanceof Error ? err.message : "Failed to update list";
        });
        throw err;
      }
    },

    deleteList: async (accountId, listId) => {
      set((state) => { state.isSaving = true; state.error = null; });
      try {
        await contactListService.deleteList(accountId, listId);
        set((state) => {
          state.lists = state.lists.filter((l) => l.id !== listId);
          if (state.currentList?.id === listId) state.currentList = null;
          state.isSaving = false;
        });
      } catch (err) {
        set((state) => {
          state.isSaving = false;
          state.error = err instanceof Error ? err.message : "Failed to delete list";
        });
        throw err;
      }
    },

    fetchListContacts: async (accountId, listId, params) => {
      set((state) => { state.isLoading = true; state.error = null; });
      try {
        const result = await contactListService.getListContacts(accountId, listId, params);
        set((state) => {
          state.listContacts = result.items;
          state.listContactsTotalCount = result.totalCount;
          state.isLoading = false;
        });
      } catch (err) {
        set((state) => {
          state.isLoading = false;
          state.error = err instanceof Error ? err.message : "Failed to load list contacts";
        });
      }
    },

    addContacts: async (accountId, listId, contactIds) => {
      set((state) => { state.isSaving = true; state.error = null; });
      try {
        await contactListService.addContactsToList(accountId, listId, contactIds);
        const refreshed = await contactListService.getList(accountId, listId);
        set((state) => {
          if (state.currentList?.id === listId) state.currentList = refreshed;
          const idx = state.lists.findIndex((l) => l.id === listId);
          if (idx !== -1) state.lists[idx] = refreshed;
          state.isSaving = false;
        });
      } catch (err) {
        set((state) => {
          state.isSaving = false;
          state.error = err instanceof Error ? err.message : "Failed to add contacts";
        });
        throw err;
      }
    },

    removeContacts: async (accountId, listId, contactIds) => {
      set((state) => { state.isSaving = true; state.error = null; });
      try {
        await contactListService.removeContactsFromList(accountId, listId, contactIds);
        set((state) => {
          state.listContacts = state.listContacts.filter((c) => !contactIds.includes(c.id));
          state.listContactsTotalCount = Math.max(0, state.listContactsTotalCount - contactIds.length);
          if (state.currentList) state.currentList.contactCount = Math.max(0, state.currentList.contactCount - contactIds.length);
          const idx = state.lists.findIndex((l) => l.id === listId);
          if (idx !== -1) state.lists[idx].contactCount = Math.max(0, state.lists[idx].contactCount - contactIds.length);
          state.isSaving = false;
        });
      } catch (err) {
        set((state) => {
          state.isSaving = false;
          state.error = err instanceof Error ? err.message : "Failed to remove contacts";
        });
        throw err;
      }
    },

    reset: () => {
      set((state) => {
        state.lists = [];
        state.currentList = null;
        state.listContacts = [];
        state.listContactsTotalCount = 0;
        state.isLoading = false;
        state.isSaving = false;
        state.error = null;
      });
    },
  })),
);
